export type IconName =
  | "account"
  | "errors"
  | "docs"
  | "menu"
  | "back"
  | "chevron"
  | "chevronDown"
  | "external"
  | "check"
  | "info"
  | "alert"
  | "close"
  | "search"
  | "copy"
  | "fullscreen"
  | "minimize"
  | "refresh"
  | "sandbox"
  | "plus"
  | "trash"
  | "eye"
  | "eyeOff"
  | "help";

export type NavGroup = "Your account" | "Build" | "Operate";

export interface NavItemData {
  key: string;
  label: string; // sidebar, drawer, breadcrumb leaf
  short: string; // mobile app bar title
  group: NavGroup;
  href: string;
  icon: IconName;
  badge?: "Soon"; // renders muted and disables the row
}

// The extension point for every future feature (7.4). 1.2/1.5/1.6 all read this.
export const navigation: NavItemData[] = [
  { key: "account", label: "Account", short: "Account", group: "Your account", href: "/app/account", icon: "account" },
  { key: "sandboxes", label: "Sandboxes", short: "Sandboxes", group: "Build", href: "/app/sandboxes", icon: "sandbox" },
  { key: "errors", label: "Explore errors", short: "Errors", group: "Build", href: "/app/explore-errors", icon: "errors" },
  { key: "docs", label: "API documentation", short: "Docs", group: "Build", href: "/app/api-documentation", icon: "docs" },
];

export interface NavGroupView {
  group: NavGroup;
  items: NavItemData[];
  showHeading: boolean;
}

// Group order follows first appearance. A heading renders only when its group
// has more than one visible item (7.4).
export function groupNavigation(items: NavItemData[]): NavGroupView[] {
  const order: NavGroup[] = [];
  const byGroup = new Map<NavGroup, NavItemData[]>();
  for (const item of items) {
    if (!byGroup.has(item.group)) {
      byGroup.set(item.group, []);
      order.push(item.group);
    }
    byGroup.get(item.group)!.push(item);
  }
  return order.map((group) => {
    const groupItems = byGroup.get(group)!;
    return { group, items: groupItems, showHeading: groupItems.length > 1 };
  });
}

export function findNavByPath(path: string): NavItemData | undefined {
  return navigation.find((item) => path.startsWith(item.href));
}

export interface Crumb {
  label: string;
  href?: string;
}

// Sub-routes have no nav item, so the leaf is named rather than guessed.
const subRouteLabels: Record<string, string> = {
  "/app/sandboxes/new": "New sandbox",
};

export function breadcrumbFor(path: string): Crumb[] {
  const root: Crumb = { label: "Developer Network", href: "/app/account" };
  const item = findNavByPath(path);
  if (!item) return [root];

  const leaf = subRouteLabels[path.replace(/[?#].*$/, "")];
  if (!leaf) return [root, { label: item.label }];

  // With a leaf below it, the item becomes the way back rather than the page.
  return [root, { label: item.label, href: item.href }, { label: leaf }];
}
